import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { ConnectionService } from '@services/connection-service';
import { LocalStorageService } from '@services/local-storage-service';
import { TimeService } from '@services/time-service';
import { AppConfig } from 'client/app/app.config';
import { FollowUpService } from '@services/followup-service';

export interface ResolveProgressFollowUpResult {
  id: string;
  followUp: any;
}

const CREATED_FOLLOWUP_MARKER_PREFIX = 'fupCreated_';

export interface SelfCheckupWindowVariant {
  start_day: string;
  end_day: string;
  class: string[];
}

export interface SelfCheckupWindowContext {
  variant: SelfCheckupWindowVariant;
  deliveredOn: Date | string;
}

export interface SelfCheckupWindowResult {
  isWithinSelfCheckupWindow: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class SelfCheckupService {
  constructor(
    private readonly connectionService: ConnectionService,
    private readonly localStorageService: LocalStorageService,
    private readonly router: Router,
    private readonly timeService: TimeService,
    private readonly appConfig: AppConfig,
    private readonly followUpService: FollowUpService,
  ) {}

  getFollowUpId(followUp: any): string | null {
    if (!followUp) return null;
    return followUp.id ?? followUp.objectId ?? null;
  }

  computeSelfCheckupWindow(params: SelfCheckupWindowContext): SelfCheckupWindowResult {
    const { variant, deliveredOn } = params;
    const deliveredDate = new Date(deliveredOn);
    const daysSinceDelivered = this.timeService.differenceInDays(new Date(), deliveredDate);

    const expStartDay = Number(variant.start_day);
    const expEndDay = Number(variant.end_day);

    const isWithinSelfCheckupWindow = daysSinceDelivered >= expStartDay && daysSinceDelivered <= expEndDay;

    return { isWithinSelfCheckupWindow };
  }

  async resolveProgressFollowUp(options: { markReady?: boolean } = {}): Promise<ResolveProgressFollowUpResult | null> {
    const followUp = await this.fetchStoredOrRecentProgressFollowUp();
    if (!followUp) return null;

    if (options.markReady) {
      await this.ensureFollowUpCreated(followUp.id);
      await this.connectionService.markFollowUpReadyStatus(followUp.id);
    }

    return { id: followUp.id, followUp };
  }

  private fetchStoredOrRecentProgressFollowUp(): any {
    const storedId = this.localStorageService.getValue('currentProgressFollowUpId');
    const pendingState = this.appConfig.Shared.Followup.State.PENDING;

    if (storedId) {
      const fromStored = this.followUpService.findFollowUpById(storedId, pendingState);
      if (fromStored) return fromStored;
    }

    const recent = this.followUpService.findMostRecentPendingFollowUp(pendingState);
    if (recent) {
      this.localStorageService.setValue('currentProgressFollowUpId', recent.id);
    }
    return recent;
  }

  async navigateToFollowUpChat(followUpId: string, options: { markReady?: boolean } = {}): Promise<boolean> {
    const { markReady = false } = options;

    if (markReady) {
      await this.ensureFollowUpCreated(followUpId);
      await this.connectionService.markFollowUpReadyStatus(followUpId);
    }

    this.localStorageService.delete('currentProgressFollowUpId');
    return this.router.navigate([`/chatV2/${followUpId}`], {
      queryParams: { type: 'followUp', fromSelfCaptureBanner: true },
    });
  }

  private async ensureFollowUpCreated(followUpId: string): Promise<void> {
    if (!followUpId) return;
    const markerKey = `${CREATED_FOLLOWUP_MARKER_PREFIX}${followUpId}`;
    if (this.localStorageService.getValue(markerKey)) return;
    await this.connectionService.createFollowUp();
    this.localStorageService.setValue(markerKey, 'true');
  }
}
